import axios from 'axios';

import { AutomatedAccountReceivablesOptions } from 'root/apps/QuoteMachineIntegration/state/AutomatedAccountReceivablesOptions';
import { datadogRum } from 'root/datadog/datadogRum';

import { AutomatedAccountReceivablesManagementType } from '../state/AutomatedAccountReceivablesManagementType';
import { AutomatedARManagementStatus } from '../state/AutomatedARManagementStatus';

// Note: GET/PUT merchant account settings for automated account receivables for the current user
export const MERCHANT_SETTINGS_URL = '/admin/invoicing/settings';

// Note: GET/PUT credit account (ie: customer) settings for auto ar
export const CREDIT_ACCOUNT_SETTINGS_URL = (creditAccountID: string) =>
  `/admin/invoicing/credit-accounts/ls-retail_${creditAccountID}/settings`;

export const CREDIT_ACCOUNT_REMINDERS_URL = (creditAccountID: string) =>
  `/admin/invoicing/v1/credit-accounts/ls-retail_${creditAccountID}/reminders`;

export default class AutomatedAccountReceivablesService {
  static async getAutomatedAccountReceivablesStatus(
    context: AutomatedAccountReceivablesManagementType,
    customerCreditAccountId?: string
  ) {
    const isAccountContext = context === AutomatedAccountReceivablesManagementType.ACCOUNT;
    const isCustomerContext =
      context === AutomatedAccountReceivablesManagementType.CUSTOMER && customerCreditAccountId;
    if (isAccountContext) {
      try {
        const { data, status } = await axios.get(MERCHANT_SETTINGS_URL);
        if (status === 200) {
          if (data.autoARSettings.enabledByDefault) {
            return AutomatedARManagementStatus.ON;
          }

          return AutomatedARManagementStatus.OFF;
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: `LS_INVOICING_ERROR: Failed to fetch automated ar status for account (${window.merchantos.account.id})`,
        });
        return AutomatedARManagementStatus.ERROR;
      }
    } else if (isCustomerContext) {
      try {
        const { data, status } = await axios.get(
          CREDIT_ACCOUNT_SETTINGS_URL(customerCreditAccountId)
        );

        if (status === 200) {
          // if we need to handle the inherited case, we can do it here with the data.autoARSettings.isInherited value.
          if (data.autoARSettings.enabled) {
            return AutomatedARManagementStatus.ON;
          }

          return AutomatedARManagementStatus.OFF;
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: `LS_INVOICING_ERROR: Failed to fetch automated ar status for customer (${customerCreditAccountId})`,
        });
        return AutomatedARManagementStatus.ERROR;
      }
    }

    return AutomatedARManagementStatus.OFF;
  }

  static async updateAutomatedAccountReceivablesStatus(
    context: AutomatedAccountReceivablesManagementType,
    automationEnabled: boolean,
    customerCreditAccountId?: string
  ) {
    const isAccountContext = context === AutomatedAccountReceivablesManagementType.ACCOUNT;
    const isCustomerContext =
      context === AutomatedAccountReceivablesManagementType.CUSTOMER && customerCreditAccountId;
    if (isAccountContext) {
      try {
        const { data, status } =
          (await axios.put(MERCHANT_SETTINGS_URL, {
            autoARSettings: {
              enabledByDefault: automationEnabled,
              // TODO: when will this get updated?
              paused: false,
            },
          })) || {};
        if (status === 200 && data) {
          const {
            autoARSettings: { enabledByDefault },
          } = data;
          return enabledByDefault;
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: `LS_INVOICING_ERROR: Failed to update automated ar status for account (${window.merchantos.account.id})`,
        });
        return AutomatedARManagementStatus.ERROR;
      }
    } else if (isCustomerContext) {
      try {
        const { data, status } = await axios.put(
          CREDIT_ACCOUNT_SETTINGS_URL(customerCreditAccountId),
          {
            autoARSettings: {
              enabled: automationEnabled,
            },
          }
        );
        if (status === 200) {
          const {
            autoARSettings: { enabled },
          } = data;
          return enabled;
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: `LS_INVOICING_ERROR: Failed to update automated ar status for customer (${customerCreditAccountId})`,
        });
        return AutomatedARManagementStatus.ERROR;
      }
    }

    return AutomatedARManagementStatus.OFF;
  }

  static async processAutoArOptions(
    options: AutomatedAccountReceivablesOptions,
    customerCreditAccountID: string
  ) {
    if (options.customerEnabled) {
      await AutomatedAccountReceivablesService.updateAutomatedAccountReceivablesStatus(
        AutomatedAccountReceivablesManagementType.CUSTOMER,
        true,
        customerCreditAccountID
      );
    }

    if (options.enabledByDefault) {
      await AutomatedAccountReceivablesService.updateAutomatedAccountReceivablesStatus(
        AutomatedAccountReceivablesManagementType.ACCOUNT,
        true
      );
    }
  }

  static async fetchActions(customerCreditAccountId: string) {
    try {
      const { data, status } = await axios.get(
        `${CREDIT_ACCOUNT_REMINDERS_URL(customerCreditAccountId)}?status=sent`,
        {}
      );
      if (status === 200) {
        return data.data;
      }
    } catch (error) {
      datadogRum.addError(error, {
        message: `LS_INVOICING_ERROR: Failed to get reminders for customer (${customerCreditAccountId})`,
      });
      return AutomatedARManagementStatus.ERROR;
    }

    return [];
  }

  // TODO:
  static getCreditAccountProfilesToReview() {
    return [];
  }
}
